/**
 * @feature quote-requests
 * @layer presentation/components
 *
 * ManagerInfoCard - 3-Column Card (담당자 정보)
 * Displays manager/contact information
 * Based on QuoteDetailPage design
 */

import { type FC } from "react";
import { Building2 } from "@semo/icons";
import { cn } from "../../../utils/cn";
import type { QuoteReviewCardProps } from "../types";

/**
 * 3-Column Card: Manager Information
 * Shows:
 * - 담당자명 (Manager name)
 * - 연락처 (Contact phone)
 */
export const ManagerInfoCard: FC<QuoteReviewCardProps> = ({ data, onEdit }) => {
  // Extract manager data
  const managerName =
    typeof data.managerName === "string" ? data.managerName : "-";
  const managerContact =
    typeof data.managerContact === "string" ? data.managerContact : "-";
  // 발주기관명은 공개 공고에만 내려온다 — 없으면 행을 그리지 않아 기존 레이아웃 유지.
  const organizationName =
    typeof data.organizationName === "string" && data.organizationName.trim()
      ? data.organizationName
      : null;

  return (
    <div className={cn("bg-white", "rounded-[20px] p-5 sm:p-6")}>
      {/* Card Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          {/* Icon */}
          <div className="w-9 h-9 rounded-[10px] bg-gray-100 flex items-center justify-center text-gray-500">
            <Building2 className="w-4.5 h-4.5" />
          </div>
          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 tracking-tight">
            담당자 정보
          </h3>
        </div>

        {/* Edit button - optional */}
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className={cn(
              "px-2 py-1",
              "text-xs font-semibold",
              "text-gray-400",
              "hover:text-secondary transition-colors",
            )}
          >
            수정
          </button>
        )}
      </div>

      {/* Card Content */}
      <div className="space-y-4">
        {organizationName && (
          <div>
            <span className="text-2xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
              발주기관
            </span>
            <span className="text-base font-semibold text-gray-900 tracking-tight">
              {organizationName}
            </span>
          </div>
        )}

        {/* Manager Name */}
        <div>
          <span className="text-2xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
            담당자명
          </span>
          <span className="text-base font-semibold text-gray-900 tracking-tight">
            {managerName}
          </span>
        </div>

        {/* Manager Contact */}
        <div>
          <span className="text-2xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
            대표 연락처
          </span>
          <span className="text-base font-medium text-gray-700">
            {managerContact}
          </span>
        </div>
      </div>
    </div>
  );
};
